import React from 'react';
import { FileIcon } from '../files/FileIcon';
import { cn } from '../../utils/cn';
import type { VaultFile } from '../../types/session.types';

export interface AutocompleteOverlayProps {
  items: VaultFile[];
  activeIndex: number;
  onSelect: (item: VaultFile) => void;
}

export const AutocompleteOverlay: React.FC<AutocompleteOverlayProps> = ({
  items,
  activeIndex,
  onSelect,
}) => {
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  if (items.length === 0) {
    return (
      <div className="absolute bottom-[calc(100%+8px)] left-0 right-0 bg-white/90 dark:bg-surface-container-low/90 backdrop-blur-md rounded-2xl border border-outline-variant/30 shadow-xl px-4 py-6 flex flex-col items-center justify-center gap-1.5 text-center z-50">
        <FileIcon filename="" className="w-8 h-8 text-secondary/40 animate-none p-1.5" />
        <h4 className="text-xs font-bold text-on-surface">No files in vault</h4>
        <p className="text-[10px] text-secondary/60 max-w-[240px]">
          Upload files to use @ mentions or search folder names.
        </p>
      </div>
    );
  }

  return (
    <div 
      className="absolute bottom-[calc(100%+8px)] left-0 right-0 max-h-60 overflow-y-auto bg-white/90 dark:bg-surface-container-low/90 backdrop-blur-md rounded-2xl border border-outline-variant/30 shadow-xl flex flex-col z-50 divide-y divide-outline-variant/10"
      role="listbox"
      aria-label="Vault file suggestions"
    >
      {items.map((item, idx) => {
        const isActive = idx === activeIndex;
        return (
          <button
            key={item.fileId}
            type="button"
            role="option"
            aria-selected={isActive}
            onClick={() => onSelect(item)}
            className={cn(
              "w-full px-4 py-2 flex items-center gap-3 text-left transition-colors border-none outline-none",
              isActive 
                ? "bg-primary/10 text-on-surface" 
                : "hover:bg-surface-container-high/60 text-secondary"
            )}
          >
            <FileIcon filename={item.filename} className="w-8 h-8 flex-shrink-0 animate-none p-1.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-on-surface truncate font-display">
                {item.filename}
              </div>
              <div className="text-[10px] text-secondary/70 truncate">
                {item.fileId}
              </div>
            </div>
            <div className="text-right text-[10px] text-secondary/60 flex-shrink-0 font-medium">
              <div>{formatSize(item.sizeBytes)}</div>
              <div className="mt-0.5">{formatDate(item.lastModified)}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
};
